import { styled } from '@mui/material/styles';
import {
  Box,
  Tooltip,
  IconButton,
  Card,
  CardActions,
  CardContent,
  Switch,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ClientContact } from '../../../data/models/client.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Contact';

const classes = {
  title: `${PREFIX}-title`,
  emailAddress: `${PREFIX}-emailAddress`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 3,

  '& .MuiCardActions-root': {
    padding: '0 !important',
    '& .MuiCardActions-spacing': {
      padding: '0 !important',
    },
  },

  '& .MuiCardContent-root': {
    color: `${theme.palette.lightTextPrimary.main} !important`,
  },

  [`& .${classes.title}`]: {
    fontWeight: 600,
    fontSize: 18,
  },

  [`& .${classes.emailAddress}`]: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
}));

interface ContactProps {
  clientContact: ClientContact;
  editContact: (contact: ClientContact) => void;
  activateContact: (contact: ClientContact) => void;
  deactivateContact: (contact: ClientContact) => void;
  deleteContact: (contact: ClientContact) => void;
  readonly: boolean;
}

export default function Contact({
  clientContact,
  editContact,
  activateContact,
  deactivateContact,
  deleteContact,
  readonly,
}: ContactProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (evt: any, contact: ClientContact) => {
    if (evt.target.checked) {
      activateContact(contact);
    } else {
      deactivateContact(contact);
    }
  };

  return (
    <StyledCard elevation={2}>
      <CardContent>
        <Box className={classes.title}>
          {clientContact.firstName} {clientContact.lastName}
        </Box>
        <Box className={classes.emailAddress}>{clientContact.emailAddress}</Box>
      </CardContent>
      {!readonly && !isSuperReaderUser && (
        <CardActions>
          <Tooltip title={t('client.editContact')}>
            <span>
              <IconButton
                data-testid="edit-contact"
                onClick={(evt) => editContact(clientContact)}
                color="primary"
                size="large">
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('client.deleteContact')}>
            <span>
              <IconButton
                data-testid="delete-contact"
                onClick={(evt) => deleteContact(clientContact)}
                color="error"
                size="large"
                disabled={clientContact.state === 'active'}>
                <DeleteForeverIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              clientContact.state === 'active'
                ? t('client.active')
                : t('client.inactive')
            }>
            <Switch
              checked={clientContact.state === 'active'}
              onChange={(evt) => handleActiveChange(evt, clientContact)}
            />
          </Tooltip>
        </CardActions>
      )}
    </StyledCard>
  );
}
